import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { HomeGame } from '@/types/game';
import type { Category } from '@shared/types/schema';
import { Loader } from '@/components/ui/loader';
import { GameFilters } from '@/components/GameFilters';
import CategoryPill from '@/components/CategoryPill';
import { useSearchParams } from 'wouter';
import { fetcher } from '@/lib/queryClient';
import { useCssVariable } from '@/hooks/use-css-variable';
import { GameCard } from '@/components/games/game-card';
import { useScrollToTop } from '@/hooks/use-scroll-to-top';

type FilterType = 'all' | 'free' | 'popular' | 'recommended';

const POPULAR_THRESHOLD = 100;

const sortGames = (games: HomeGame[], activeFilter: FilterType): HomeGame[] => {
  if (activeFilter === 'popular') {
    return [...games].sort((a, b) => (b?.initialViews ?? 0) - (a?.initialViews ?? 0));
  }
  return games;
};

const filterGames = (
  games: HomeGame[],
  activeFilter: FilterType,
  selectedCategory: number | null,
): HomeGame[] => {
  return games
    .filter((game) => (selectedCategory ? game.categoryIds?.includes(selectedCategory) : true))
    .filter((game) => {
      switch (activeFilter) {
        case 'free':
          return game.isFree;
        case 'popular':
          return (game.initialViews ?? 0) > POPULAR_THRESHOLD;
        case 'recommended':
          return game.isRecommended;
        default:
          return true;
      }
    });
};

export default function Games() {
  useScrollToTop();
  useCssVariable('--main-container-max-width', '100%', { removeOnUnmount: true });

  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const { data: games = [], isLoading } = useQuery<HomeGame[]>({
    queryKey: ['/api/games'],
    queryFn: fetcher,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['/api/categories'],
    queryFn: fetcher,
  });

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const urlCategoryId = searchParams.get('categoryId');
    if (urlCategoryId) {
      setSelectedCategory(Number(urlCategoryId));
    }
  }, [searchParams]);

  const filteredGames = useMemo(() => {
    const filtered = filterGames(games, activeFilter, selectedCategory);
    return sortGames(filtered, activeFilter);
  }, [games, activeFilter, selectedCategory]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <main className="main-container pt-20">
      <h1 className="text-5xl font-bold mb-8">Категории</h1>
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          <CategoryPill
            key="all"
            label="Все игры"
            isActive={selectedCategory === null}
            onClick={() => setSelectedCategory(null)}
          />
          {categories.map((category) => (
            <CategoryPill
              key={category.id}
              label={category.name}
              isActive={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
            />
          ))}
        </div>
      </div>

      <div className="mb-8">
        <GameFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-y-5 gap-x-4 mb-8">
        {filteredGames.map((game: HomeGame) => (
          <GameCard key={game.id} game={game} />
        ))}
      </div>
      {filteredGames.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">Игры не найдены</div>
      )}
    </main>
  );
}
